import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Layout } from 'react-grid-layout';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Person, SeatPosition } from '../types';

const DEPARTMENT_COLORS: Record<string, string> = {
  'pxd': 'rgba(128,128,128,0.3)',
  '프리랜서': 'rgba(0,123,255,0.3)',
  '외주사': 'rgba(0,123,255,0.3)',
  '계약직': 'rgba(0,123,255,0.3)',
  '인턴': 'rgba(255,153,0,0.3)',
};

function getColorByDepartment(department: string) {
  return DEPARTMENT_COLORS[department] || 'rgba(128,128,128,0.3)';
}

interface SeatGridProps {
  persons: Person[];
  positions: SeatPosition[];
  onPositionChange: (positions: SeatPosition[]) => void;
  isEditMode: boolean;
  onPersonEdit: (person: Person) => void;
  onPersonDelete: (id: number) => void;
  onPersonDuplicate: (person: Person) => void;
  legendFilters: {
    pxd: boolean;
    external: boolean;
    intern: boolean;
    planned: boolean;
  };
  handleLegendToggle: (type: 'pxd' | 'external' | 'intern' | 'planned') => void;
}

const SeatGrid: React.FC<SeatGridProps> = React.memo(({
  persons,
  positions,
  onPositionChange,
  isEditMode,
  onPersonEdit,
  onPersonDelete,
  onPersonDuplicate,
  legendFilters,
  handleLegendToggle
}) => {
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, person: Person} | null>(null);
  const [localLayout, setLocalLayout] = useState<Layout[]>(() => {
    return positions.map(position => ({
      i: position.personId.toString(),
      x: position.x,
      y: position.y,
      w: 2,
      h: 2,
    }));
  });
  const isLayoutChanging = React.useRef(false);
  const [confirmDelete, setConfirmDelete] = useState<Person | null>(null);
  const [hoveredPerson, setHoveredPerson] = useState<Person | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartTime = useRef<number>(0);
  const dragStartPos = useRef<{x: number, y: number}>({x: 0, y: 0});
  const clickedPerson = useRef<Person | null>(null);
  const isRightClick = useRef<boolean>(false);
  const preventLayoutChange = useRef<boolean>(false);

  // 메모이제이션된 계산 값들
  const localLayoutMemo = useMemo(() => {
    return positions.map(position => ({
      i: position.personId.toString(),
      x: position.x,
      y: position.y,
      w: 2,
      h: 2,
    }));
  }, [positions]);

  // positions 변경 시 localLayout 업데이트
  useEffect(() => {
    if (!isLayoutChanging.current) {
      const prevLength = localLayout.length;
      const newLayout = positions.map(pos => ({
        i: pos.personId.toString(),
        x: pos.x,
        y: pos.y,
        w: 2,
        h: 2,
      }));
      
      // 새 인원이 추가된 경우 GridLayout의 자동 repositioning 차단
      if (newLayout.length > prevLength) {
        // GridLayout의 자동 repositioning 차단
        preventLayoutChange.current = true;
        
        setTimeout(() => {
          preventLayoutChange.current = false;
        }, 1000);
      }
      
      setLocalLayout(newLayout);
    }
  }, [positions]);

  const handleContextMenu = useCallback((e: React.MouseEvent, person: Person) => {
    if (!isEditMode) return; // 수정 모드가 아니면 아무런 액션 없음
    
    e.preventDefault(); // 수정 모드에서만 브라우저 기본 메뉴 차단
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      person,
    });
  }, [isEditMode]);

  const handleDeleteClick = () => {
    if (contextMenu) {
      setConfirmDelete(contextMenu.person);
    }
    setContextMenu(null);
  };

  const handleEditPerson = useCallback((person: Person) => {
    onPersonEdit(person);
    setContextMenu(null);
  }, [onPersonEdit]);

  const handleDuplicatePersonCallback = useCallback((person: Person) => {
    onPersonDuplicate(person);
    setContextMenu(null);
  }, [onPersonDuplicate]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDelete(null);
  }, []);

  const handleLayoutChange = useCallback((newLayout: Layout[]) => {
    if (!isEditMode) return;
    
    // 새 인원 추가 시 자동 repositioning 차단
    if (preventLayoutChange.current) {
      return;
    }
    
    isLayoutChanging.current = true;
    setLocalLayout(newLayout);
    
    const updatedPositions = positions.map(pos => {
      // layout의 i는 personId.toString()이므로 personId로 매칭
      const layoutItem = newLayout.find(item => Number(item.i) === pos.personId);
      if (layoutItem) {
        return {
          ...pos,
          x: layoutItem.x,
          y: layoutItem.y,
        };
      }
      return pos;
    });
    
    onPositionChange(updatedPositions);
    setTimeout(() => {
      isLayoutChanging.current = false;
    }, 0);
  }, [isEditMode, positions, onPositionChange, persons]);

  // 날짜 관련 함수들 메모이제이션
  const formatEndDate = useCallback((dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `~${d.getMonth() + 1}/${d.getDate()}`;
  }, []);

  const formatStartDate = useCallback((dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getMonth() + 1}/${d.getDate()}~`;
  }, []);

  const getDisplayDate = useCallback((person: Person) => {
    if (person.startDate) {
      return formatStartDate(person.startDate);
    }
    return formatEndDate(person.endDate);
  }, [formatStartDate, formatEndDate]);

  const isEndDateNear = useCallback((person: Person) => {
    if (person.startDate) return false;
    
    if (person.endDate) {
      const endDate = new Date(person.endDate);
      const today = new Date();
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays <= 14;
    }
    return false;
  }, []);

  // 스타일 관련 함수들 메모이제이션
  const getPersonClasses = useCallback((person: Person) => {
    const baseClass = 'pb';
    const nearEndClass = isEndDateNear(person) ? ' pb-near' : '';
    const plannedClass = person.isPlanned ? ' pb-planned' : '';
    
    let deptClass = '';
    switch(person.department) {
      case 'pxd':
        deptClass = ' pb-pxd';
        break;
      case '인턴':
        deptClass = ' pb-intern';
        break;
      case '프리랜서':
      case '외주사':
      case '계약직':
        deptClass = ' pb-ext';
        break;
      case '레이블':
        deptClass = ' pb-label';
        break;
      default:
        deptClass = ' pb-pxd';
    }
    
    const tempClass = person.usageType === '임시석' ? ' pb-temp' : '';
    
    return baseClass + nearEndClass + plannedClass + deptClass + tempClass;
  }, [isEndDateNear]);

  const getDateClasses = useCallback((person: Person) => {
    const baseClass = 'pd';
    const animateClass = isEndDateNear(person) ? ' pd-anim' : '';
    
    return baseClass + animateClass;
  }, [isEndDateNear]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleEditClick = () => {
    if (contextMenu && onPersonEdit) {
      onPersonEdit(contextMenu.person);
    }
    setContextMenu(null);
  };

  const handleDuplicateClick = () => {
    if (contextMenu && onPersonDuplicate) {
      onPersonDuplicate(contextMenu.person);
    }
    setContextMenu(null);
  };

  const handleConfirmDelete = () => {
    if (confirmDelete && onPersonDelete) {
      onPersonDelete(confirmDelete.id);
    }
    setConfirmDelete(null);
  };

  const handlePersonClick = useCallback((person: Person) => {
    // 수정 모드에서는 드래그와 구분 필요
    if (isEditMode) {
      // 드래그 중이거나 최근에 드래그했다면 클릭 이벤트 무시
      if (isDragging) return;
      
      const timeSinceDragStart = Date.now() - dragStartTime.current;
      if (timeSinceDragStart < 200) return; // 200ms 이내면 드래그로 간주
    }
    
    onPersonEdit(person);
  }, [isEditMode, isDragging, onPersonEdit]);

  const handleMouseDown = useCallback((e: React.MouseEvent, person: Person) => {
    dragStartTime.current = Date.now();
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    clickedPerson.current = person;
    isRightClick.current = e.button === 2; // 우클릭 감지
    
    if (isEditMode) {
      setIsDragging(false);
    }
  }, [isEditMode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // 수정 모드에서만 드래그 감지 필요
    if (!isEditMode) return;
    
    const currentPos = { x: e.clientX, y: e.clientY };
    const distance = Math.sqrt(
      Math.pow(currentPos.x - dragStartPos.current.x, 2) + 
      Math.pow(currentPos.y - dragStartPos.current.y, 2)
    );
    
    // 5px 이상 움직이면 드래그로 간주
    if (distance > 5) {
      setIsDragging(true);
    }
  }, [isEditMode]);

  const handleMouseUp = useCallback((e: React.MouseEvent, person?: Person) => {
    const currentPerson = clickedPerson.current;
    
    if (!currentPerson || currentPerson.id !== person?.id) {
      // 클릭 정보 초기화
      setTimeout(() => {
        if (isEditMode) {
          setIsDragging(false);
        }
        clickedPerson.current = null;
        isRightClick.current = false;
      }, 50);
      return;
    }

    const timeSinceDragStart = Date.now() - dragStartTime.current;
    const currentPos = { x: e.clientX, y: e.clientY };
    const distance = Math.sqrt(
      Math.pow(currentPos.x - dragStartPos.current.x, 2) + 
      Math.pow(currentPos.y - dragStartPos.current.y, 2)
    );

    // 짧은 시간에 적은 거리 움직임이면 클릭으로 간주 (우클릭 제외)
    if (timeSinceDragStart < 300 && distance < 5 && !isRightClick.current) {
      e.preventDefault();
      e.stopPropagation();
      onPersonEdit(currentPerson);
    }

    // 상태 초기화
    setTimeout(() => {
      if (isEditMode) {
        setIsDragging(false);
      }
      clickedPerson.current = null;
      isRightClick.current = false;
    }, 50);
  }, [isEditMode, onPersonEdit]);

  return (
    <div className="sg-container">
      <style>
        {`
          :root {
            --cc-pxd: rgba(103,143,255,0.26);
            --cc-ext: rgba(40,183,101,0.55);
            --cc-intern: rgba(255,153,0,0.8);
            --c-temp: rgba(255,153,0,0.3);
            --b-pxd: rgba(103,143,255,0.18);
            --b-ext: rgba(40,183,101,0.4);
            --b-intern: rgba(255,153,0,0.35);
          }
          
          .sg-container {
            width: 1920px;
            height: 800px;
            background: url('/floor-plan.png') left top/cover no-repeat;
            position: relative;
          }
          
          /* react-grid-layout 애니메이션 완전 비활성화 */
          .react-grid-layout {
            position: relative;
          }
          
          .react-grid-item {
            transition: none !important;
            transform: none !important;
          }
          
          .react-grid-item.react-grid-placeholder {
            background: rgba(0, 0, 0, 0.1);
            border-radius: 4px;
            transition: none !important;
          }
          
          .react-grid-item > .react-resizable-handle {
            position: absolute;
            width: 20px;
            height: 20px;
            bottom: 0;
            right: 0;
            background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0ibm9uZSIvPjxnPjxwYXRoIGQ9Im0xNiwyMGwwLDIwbDIwLDBsMCwtMjBsLTIwLDBabTEwLDE2bC02LDBsMCwtMTBsNiwwbDAsMTB6IiBmaWxsPSIjZGRkIi8+PC9nPjwvc3ZnPg==') no-repeat;
            background-size: contain;
            cursor: se-resize;
          }
          
          .lg-container {
            position: fixed;
            top: 16px;
            left: 16px;
            z-index: 50;
            display: flex;
            align-items: center;
            gap: 16px;
            background: rgba(255,255,255,0.9);
            backdrop-filter: blur(12px);
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.1);
            padding: 8px 16px;
            border: 1px solid rgba(229,231,235,1);
          }
          
          .lg-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            outline: none;
            border: 2px solid transparent;
            background: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: all 0.2s ease;
          }
          
          .lg-btn:hover { 
            background: rgba(0,0,0,0.05);
            // border-color: rgba(0,0,0,0.1);
          }
          
          .lg-btn.disabled { 
            opacity: 0.4; 
            border-color: transparent;
            background: rgba(156,163,175,0.1);
          }
          
          .lg-btn.disabled:hover {
            background: rgba(156,163,175,0.15);
            border-color: transparent;
          }
          
          .lg-color {
            width: 24px;
            height: 16px;
            border-radius: 4px;
          }
          
          .lg-pxd { background: var(--cc-pxd); border: 1px solid var(--b-pxd); }
          .lg-ext { background: var(--cc-ext); border: 1px solid var(--b-ext); }
          .lg-intern { background: var(--cc-intern); border: 1px solid var(--b-intern); }
          .lg-planned { 
            width: 0; 
            height: 0; 
            border-left: 8px solid transparent; 
            border-top: 8px solid rgb(246,59,226); 
            border-radius: 0;
          }
          
          .lg-text { font-size: 12px; font-weight: 500; }
          
          .pb {
            backdrop-filter: blur(1px);
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            user-select: none;
            border-radius: 4px;
            color: black;
            min-width: 46px;
            min-height: 30px;
            position: relative;
            border: 1px solid;
          }

          .pb:hover {
            border-color: rgba(0,0,0,0.6);
            backdrop-filter: blur(4px);
          }
          .pb:hover .pd {
            color: rgba(0,0,0,0.9);
          }
          
          .pb-pxd { background: var(--cc-pxd); border-color: var(--b-pxd); }
          .pb-ext { background: var(--cc-ext); border-color: var(--b-ext); }
          .pb-intern { background: var(--cc-intern); border-color: var(--b-intern); }
          .pb-label { background: #444; border-color: #333; color: white; }
          .pb-label .pn { color: white; }
          .pb-label .pd { color: rgba(255,255,255,0.7); }
          .pb-temp { background: var(--c-temp); }
          
          .pb-near {
            border: 2px solid rgba(0,0,0,0.6);
            /* animation: bp 1s ease-in-out infinite; */
          }
          
          .pb-planned {
            border-color: rgb(246,59,226) !important;
          }
          
          .pn {
            font-weight: bold;
            font-size: 12px;
            color: black;
            width: 100%;
            text-align: center;
            user-select: none;
            line-height: 1.25;
            white-space: normal;
            overflow: visible;
          }
          
          .pd {
            font-size: 11px;
            font-weight: 600;
            width: 100%;
            text-align: center;
            user-select: none;
            line-height: 1.25;
            margin-top: -2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: rgba(0,0,0,0.27);
          }
          
          .pd-anim {
            color: rgba(0,0,0,0.9);
            /* animation: op 1s ease-in-out infinite; */
          }
          
          .pp {
            position: absolute;
            top: 0;
            right: 0;
            width: 0;
            height: 0;
            border-left: 12px solid transparent;
            border-top: 12px solid rgb(246,59,226);
            z-index: 10;
          }
          
          .ptt {
            position: absolute;
            z-index: 50;
            padding: 4px 8px;
            font-size: 12px;
            font-weight: 700;
            color: #000;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            margin-top: -4px;
            white-space: nowrap;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            z-index: 1000;
          }
          
          .cm {
            position: fixed;
            background: white;
            border: 1px solid rgba(0,0,0,0.1);
            border-radius: 4px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 1000;
            min-width: 120px;
          }
          
          .cm-item {
            display: block;
            width: 100%;
            padding: 8px 16px;
            border: none;
            background: none;
            text-align: left;
            cursor: pointer;
            font-size: 14px;
            color: rgba(0,0,0,0.87);
          }
          
          .cm-item:hover { background: rgba(0,0,0,0.04); }
          .cm-item.del { color: rgb(211,47,47); }
          
          .do {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1001;
          }
          
          .d {
            background: white;
            border-radius: 4px;
            padding: 0;
            min-width: 280px;
            max-width: 400px;
          }
          
          .dt {
            padding: 16px 24px;
            margin: 0;
            font-size: 20px;
            font-weight: 500;
            color: rgba(0,0,0,0.87);
          }
          
          .da {
            padding: 8px;
            display: flex;
            gap: 8px;
            justify-content: flex-end;
          }
          
          .db {
            padding: 6px 16px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 0.02857em;
          }
          
          .db-c {
            background: none;
            color: rgb(25,118,210);
          }
          
          .db-c:hover { background: rgba(25,118,210,0.04); }
          
          .db-d {
            background: none;
            color: rgb(211,47,47);
          }
          
          .db-d:hover { background: rgba(211,47,47,0.04); }
          
          /* 애니메이션 제거로 인한 주석 처리
          @keyframes op {
            0% { opacity: 0.3; }
            50% { opacity: 0.8; }
            100% { opacity: 0.3; }
          }
          
          @keyframes bp {
            0% { border-width: 2px; border-opacity: 0.3; }
            50% { border-width: 2px; border-opacity: 0.8; }
            100% { border-width: 2px; border-opacity: 0.3; }
          }
          */
        `}
      </style>
      

      
      <GridLayout
        className="layout"
        layout={localLayout}
        cols={96}
        rowHeight={9}
        width={1800}
        onLayoutChange={handleLayoutChange}
        isDraggable={isEditMode}
        isResizable={false}
        compactType={null}
        preventCollision={false}
        margin={[0, 0]}
        useCSSTransforms={false}
        transformScale={1}
        autoSize={false}
      >
        {positions.map(position => {
          const person = persons.find(p => p.id === position.personId);
          if (!person) return null;
          
          return (
            <div key={position.personId.toString()} className="w-full h-full relative">
              <div
                className={getPersonClasses(person)}
                onClick={() => handlePersonClick(person)}
                onMouseDown={(e) => handleMouseDown(e, person)}
                onMouseMove={handleMouseMove}
                onMouseUp={(e) => handleMouseUp(e, person)}
                onContextMenu={e => handleContextMenu(e, person)}
                onMouseEnter={() => setHoveredPerson(person)}
                onMouseLeave={() => setHoveredPerson(null)}
              >
                {person.isPlanned && <div className="pp" />}
                
                <div className="pn">{person.name}</div>
                
                <div className={getDateClasses(person)}>
                  {getDisplayDate(person)}
                </div>
                
                {/* 외주사/인턴/계약직/프리랜서 프로젝트명 툴팁 */}
                {hoveredPerson?.id === person.id && ['외주사', '인턴', '계약직', '프리랜서'].includes(person.department) && person.projectName && (
                  <div className="ptt">
                    {person.projectName}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </GridLayout>
      
      {/* 컨텍스트 메뉴 */}
      {contextMenu && (
        <div 
          className="cm"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <button className="cm-item" onClick={handleEditClick}>
            수정
          </button>
          <button className="cm-item" onClick={handleDuplicateClick}>
            복제
          </button>
          <button className="cm-item del" onClick={handleDeleteClick}>
            삭제
          </button>
        </div>
      )}
      
      {/* 삭제 확인 다이얼로그 */}
      {confirmDelete && (
        <div className="do">
          <div className="d">
            <h2 className="dt">정말 삭제하시겠습니까?</h2>
            <div className="da">
              <button className="db db-c" onClick={handleCancelDelete}>
                취소
              </button>
              <button className="db db-d" onClick={handleConfirmDelete}>
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SeatGrid;